import { HStack, Stack, Text } from '@chakra-ui/react';
import { KeyRound } from 'lucide-react';

import { Button } from '@/components/core';
import { snackbar } from '@/components/features/Snackbar';
import { FONTS_OPACITY } from '@/constant';
import { PasskeyError, passkeyErrorMessage } from '@/services/passkeys';
import { useRegisterPasskey } from '@/store/passkeys';

/** "Sign in faster next time" (Connect #498). Shown once after a password sign-in. */
export function PasskeyEnrolPrompt({
  onDone,
  onDismiss,
}: {
  /** A passkey was added. */
  onDone: () => void;
  /** "Not now": don't ask again on this device. */
  onDismiss: () => void;
}) {
  const { mutateAsync: register, isPending } = useRegisterPasskey();

  const addPasskey = async () => {
    try {
      const created = await register(undefined);
      snackbar({
        title: 'Passkey added',
        description: `Next time, sign in with ${created.name}.`,
        type: 'success',
      });
      onDone();
    } catch (error) {
      // A closed browser prompt leaves the dialog open, so the User can try again or say not now.
      const message = passkeyErrorMessage(
        error instanceof PasskeyError ? error.code : 'unavailable'
      );
      if (message) {
        snackbar({ title: 'Error', description: message, type: 'error' });
      }
    }
  };

  return (
    <Stack data-cy='passkey-enrol-prompt' gap={6} p={8}>
      <Stack>
        <HStack gap={2}>
          <KeyRound size={24} opacity={FONTS_OPACITY} />
          <Text size='headingPrimary'>Sign in faster next time</Text>
        </HStack>
        <Text size='secondaryRegular' opacity={FONTS_OPACITY}>
          Add a passkey to sign in with Face ID, Touch ID, Windows Hello or your
          password manager instead of your password.
        </Text>
      </Stack>
      <HStack>
        <Button
          data-cy='passkey-enrol-add'
          size='lg'
          leftIcon={<KeyRound size={16} />}
          isLoading={isPending}
          onClick={() => void addPasskey()}
        >
          Add a passkey
        </Button>
        <Button
          data-cy='passkey-enrol-dismiss'
          size='lg'
          variant='ghost'
          isDisabled={isPending}
          onClick={onDismiss}
        >
          Not now
        </Button>
      </HStack>
    </Stack>
  );
}
